import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import type {
  ThreatDetectionMetrics,
  ThreatEvent,
  AuthMetrics,
  AuthEvent,
  AuthEventType,
  AuthMethod,
  AuthResult,
  CryptoOperation,
  IdentityMetrics,
  Identity,
  ComplianceMetrics,
  AuditEvent,
  SecurityDashboardData,
  SecurityAlert,
  SecurityIncident,
  AnomalyDetection,
  NetworkSecurityState,
} from '../types/security';
import { apiClient } from '../services/api'

// Helper to create stable query keys by serializing filters
function serializeFilters(filters?: any): string {
  if (!filters) return '';
  
  // Convert dates to ISO strings for stable comparison
  const serialized = JSON.parse(JSON.stringify(filters, (key, value) => {
    if (value instanceof Date) {
      return value.toISOString();
    }
    return value;
  }));
  
  // Sort keys for consistent ordering
  return JSON.stringify(serialized, Object.keys(serialized).sort());
}

// Custom query keys for security data
export const SECURITY_QUERY_KEYS = {
  dashboard: ['security', 'dashboard'] as const,
  threatDetection: ['security', 'threat-detection'] as const,
  threatEvents: (filters?: any) => ['security', 'threat-events', serializeFilters(filters)] as const,
  authMetrics: ['security', 'auth-metrics'] as const,
  authEvents: (filters?: any) => ['security', 'auth-events', serializeFilters(filters)] as const,
  cryptoOperations: (filters?: any) => ['security', 'crypto-operations', serializeFilters(filters)] as const,
  identityMetrics: ['security', 'identity-metrics'] as const,
  identities: (filters?: any) => ['security', 'identities', serializeFilters(filters)] as const,
  complianceMetrics: ['security', 'compliance-metrics'] as const,
  auditEvents: (filters?: any) => ['security', 'audit-events', serializeFilters(filters)] as const,
  incidents: (filters?: any) => ['security', 'incidents', serializeFilters(filters)] as const,
  alerts: (filters?: any) => ['security', 'alerts', serializeFilters(filters)] as const,
  anomalies: (filters?: any) => ['security', 'anomalies', serializeFilters(filters)] as const,
  networkSecurity: ['security', 'network-security'] as const,
};

// Hook for comprehensive security dashboard data
export function useSecurityDashboard(refreshInterval: number = 60000) { // 60 seconds default
  return useQuery({
    queryKey: SECURITY_QUERY_KEYS.dashboard,
    queryFn: async (): Promise<SecurityDashboardData> => {
      console.log('[SECURITY] Dashboard query start');
      console.log('[SECURITY] Query start time:', new Date().toISOString());
      const overallStart = performance.now();
      
      // Fetch all security metrics in parallel
      console.log('[SECURITY] Starting parallel API calls...');
      const apiCallsStart = performance.now();
      
      const [
        threatDetection,
        authMetrics,
        identityMetrics,
        complianceMetrics,
        recentThreats,
        recentAuthEvents,
        recentCryptoOps,
        recentAuditEvents,
        alerts,
      ] = await Promise.all([
        fetchThreatDetectionMetrics().then(result => {
          console.log('[SECURITY] Threat Detection Metrics loaded');
          return result;
        }),
        fetchAuthMetrics().then(result => {
          console.log('[SECURITY] Auth Metrics loaded');
          return result;
        }),
        fetchIdentityMetrics().then(result => {
          console.log('[SECURITY] Identity Metrics loaded');
          return result;
        }),
        fetchComplianceMetrics().then(result => {
          console.log('[SECURITY] Compliance Metrics loaded');
          return result;
        }),
        fetchThreatEvents({ limit: 10 }).then(result => {
          console.log('[SECURITY] Recent Threats loaded:', result.length, 'items');
          return result;
        }),
        fetchAuthEvents({ limit: 10 }).then(result => {
          console.log('[SECURITY] Recent Auth Events loaded:', result.length, 'items');
          return result;
        }),
        fetchCryptoOperations({ limit: 10 }).then(result => {
          console.log('[SECURITY] Recent Crypto Ops loaded:', result.length, 'items');
          return result;
        }),
        fetchAuditEvents({ limit: 10 }).then(result => {
          console.log('[SECURITY] Recent Audit Events loaded:', result.length, 'items');
          return result;
        }),
        fetchSecurityAlerts({ limit: 20, unresolved: true }).then(result => {
          console.log('[SECURITY] Security Alerts loaded:', result.length, 'items');
          return result;
        }),
      ]);

      const apiCallsTime = performance.now() - apiCallsStart;
      console.log('[SECURITY] All API calls completed in', apiCallsTime.toFixed(2), 'ms');
      
      const dashboardData = {
        threatDetection,
        authentication: authMetrics,
        // GH #2704: no `cryptography` block. The route that produced it was
        // removed as fabricated, and an invented stand-in is what the removal
        // exists to delete, so the field is simply absent.
        identity: identityMetrics,
        compliance: complianceMetrics,
        recentEvents: {
          threats: recentThreats,
          authEvents: recentAuthEvents,
          cryptoOps: recentCryptoOps,
          auditEvents: recentAuditEvents,
        },
        alerts,
      };
      
      const overallTime = performance.now() - overallStart;
      console.log('[SECURITY] Dashboard query complete in', overallTime.toFixed(2), 'ms');
      console.log('[SECURITY] Dashboard data summary:', {
        threatCount: threatDetection.overview.totalThreats,
        alertCount: alerts.length,
        authSuccess: authMetrics.overview.successRate + '%'
      });
      
      return dashboardData;
    },
    refetchInterval: refreshInterval,
    staleTime: 30000, // Consider data stale after 30 seconds
  });
}

// Threat Detection Hooks
export function useThreatDetectionMetrics(refreshInterval: number = 30000) { // 30 seconds
  return useQuery({
    queryKey: SECURITY_QUERY_KEYS.threatDetection,
    queryFn: fetchThreatDetectionMetrics,
    refetchInterval: refreshInterval,
  });
}

export function useThreatEvents(filters?: {
  severity?: string[]
  type?: string[]
  status?: string[]
  nodeId?: string
  timeRange?: { start: Date | string; end: Date | string }
  limit?: number
}) {
  return useQuery({
    queryKey: SECURITY_QUERY_KEYS.threatEvents(filters),
    queryFn: () => fetchThreatEvents(filters),
    enabled: !!filters, // Only run when filters are provided
    refetchInterval: false, // Disable automatic refetch
    refetchOnWindowFocus: false, // Disable refetch on window focus
    refetchOnMount: false, // Disable refetch on mount
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    onError: (error) => {
      console.error('Failed to fetch threat events:', error);
    },
  });
}

// Authentication & Authorization Hooks
export function useAuthMetrics(refreshInterval: number = 30000) { // 30 seconds
  return useQuery({
    queryKey: SECURITY_QUERY_KEYS.authMetrics,
    queryFn: fetchAuthMetrics,
    refetchInterval: refreshInterval,
  });
}

export function useAuthEvents(filters?: {
  result?: string[]
  method?: string[]
  userId?: string
  nodeId?: string
  timeRange?: { start: Date | string; end: Date | string }
  limit?: number
}) {
  return useQuery({
    queryKey: SECURITY_QUERY_KEYS.authEvents(filters),
    queryFn: () => fetchAuthEvents(filters),
    refetchInterval: 30000, // 30 seconds
  });
}

// Cryptographic Operations Hooks
// GH #2704 removed useCryptoMetrics with the GET it wrapped. Only the
// operations listing survives, and it reads audit rows the removed
// key-rotation route had already written.
export function useCryptoOperations(filters?: {
  type?: string[]
  algorithm?: string[]
  nodeId?: string
  timeRange?: { start: Date | string; end: Date | string }
  limit?: number
}) {
  return useQuery({
    queryKey: SECURITY_QUERY_KEYS.cryptoOperations(filters),
    queryFn: () => fetchCryptoOperations(filters),
    refetchInterval: 30000, // 30 seconds
  });
}

// Identity Management Hooks
export function useIdentityMetrics(refreshInterval: number = 60000) { // 60 seconds
  return useQuery({
    queryKey: SECURITY_QUERY_KEYS.identityMetrics,
    queryFn: fetchIdentityMetrics,
    refetchInterval: refreshInterval,
  });
}

export function useIdentities(filters?: {
  status?: string[]
  verificationLevel?: string[]
  nodeId?: string
  limit?: number
}) {
  return useQuery({
    queryKey: SECURITY_QUERY_KEYS.identities(filters),
    queryFn: () => fetchIdentities(filters),
    refetchInterval: 30000,
  });
}

// Compliance & Audit Hooks
export function useComplianceMetrics(refreshInterval: number = 120000) { // 2 minutes
  return useQuery({
    queryKey: SECURITY_QUERY_KEYS.complianceMetrics,
    queryFn: fetchComplianceMetrics,
    refetchInterval: refreshInterval,
  });
}

export function useAuditEvents(filters?: {
  category?: string[]
  riskLevel?: string[]
  actor?: string
  nodeId?: string
  timeRange?: { start: Date | string; end: Date | string }
  limit?: number
}) {
  return useQuery({
    queryKey: SECURITY_QUERY_KEYS.auditEvents(filters),
    queryFn: () => fetchAuditEvents(filters),
    refetchInterval: 60000, // 60 seconds
  });
}

// Security Incidents & Alerts
export function useSecurityIncidents(filters?: {
  severity?: string[]
  status?: string[]
  assignedTo?: string
  limit?: number
}) {
  return useQuery({
    queryKey: SECURITY_QUERY_KEYS.incidents(filters),
    queryFn: () => fetchSecurityIncidents(filters),
    refetchInterval: 30000,
  });
}

export function useSecurityAlerts(filters?: {
  severity?: string[]
  type?: string[]
  unresolved?: boolean
  nodeId?: string
  limit?: number
}) {
  return useQuery({
    queryKey: SECURITY_QUERY_KEYS.alerts(filters),
    queryFn: () => fetchSecurityAlerts(filters),
    refetchInterval: 30000, // 30 seconds
  });
}

// Anomaly Detection
export function useAnomalyDetections(filters?: {
  severity?: string[]
  modelId?: string
  nodeId?: string
  timeRange?: { start: Date | string; end: Date | string }
  limit?: number
}) {
  return useQuery({
    queryKey: SECURITY_QUERY_KEYS.anomalies(filters),
    queryFn: () => fetchAnomalyDetections(filters),
    enabled: !!filters, // Only run when filters are provided
    refetchInterval: false, // Disable automatic refetch
    refetchOnWindowFocus: false, // Disable refetch on window focus
    refetchOnMount: false, // Disable refetch on mount
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
}

// Network Security
export function useNetworkSecurity(refreshInterval: number = 20000) {
  return useQuery({
    queryKey: SECURITY_QUERY_KEYS.networkSecurity,
    queryFn: fetchNetworkSecurity,
    refetchInterval: refreshInterval,
  });
}

// Real-time WebSocket Hook for Security Events
export function useSecurityWebSocket(onEvent: (event: any) => void) {
  const queryClient = useQueryClient();
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const connect = useCallback(() => {
    // Import the WebSocket service
    import('../services/websocket-unified').then(({ wsService }) => {
      // Subscribe to security events
      unsubscribeRef.current = wsService.subscribe('security:event', (data) => {
        onEvent(data);

        // Invalidate relevant queries based on event type. security:event carries
        // SecuritySocketEvent (socket-cloud.openapi.yaml), whose one type is
        // 'auth:event' with an AuthEvent as data; apps/api server.ts emits it per
        // authentication audit row (Robert's card ruling 2026-10-16, "Fix").
        switch (data.type) {
          case 'threat_detected':
            queryClient.invalidateQueries({ queryKey: SECURITY_QUERY_KEYS.threatDetection });
            queryClient.invalidateQueries({ queryKey: SECURITY_QUERY_KEYS.threatEvents() });
            break;
          case 'auth:event':
            queryClient.invalidateQueries({ queryKey: SECURITY_QUERY_KEYS.authMetrics });
            queryClient.invalidateQueries({ queryKey: SECURITY_QUERY_KEYS.authEvents() });
            break;
          case 'crypto_operation':
            queryClient.invalidateQueries({ queryKey: SECURITY_QUERY_KEYS.cryptoOperations() });
            break;
          case 'audit_event':
            queryClient.invalidateQueries({ queryKey: SECURITY_QUERY_KEYS.auditEvents() });
            break;
          case 'security_alert':
            queryClient.invalidateQueries({ queryKey: SECURITY_QUERY_KEYS.alerts() });
            break;
          case 'anomaly_detected':
            queryClient.invalidateQueries({ queryKey: SECURITY_QUERY_KEYS.anomalies() });
            break;
        }

        // Always refresh dashboard data
        queryClient.invalidateQueries({ queryKey: SECURITY_QUERY_KEYS.dashboard });
      });
    });
  }, [onEvent, queryClient]);

  const disconnect = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
  }, []);

  useEffect(() => {
    connect();
    return disconnect;
  }, [connect, disconnect]);

  return { connect, disconnect };
}

// Security Action Hooks (mutations)
export function useSecurityActions() {
  const queryClient = useQueryClient();

  const quarantineNode = useCallback(async (nodeId: string, reason: string) => {
    const response = await apiClient.request(`/security/nodes/${nodeId}/quarantine`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
    
    if (response.success) {
      queryClient.invalidateQueries({ queryKey: SECURITY_QUERY_KEYS.networkSecurity });
    }
    
    return response;
  }, [queryClient]);

  const acknowledgeAlert = useCallback(async (alertId: string) => {
    const response = await apiClient.request(`/security/alerts/${alertId}/acknowledge`, {
      method: 'POST',
    });
    
    if (response.success) {
      queryClient.invalidateQueries({ queryKey: SECURITY_QUERY_KEYS.alerts() });
    }
    
    return response;
  }, [queryClient]);

  const resolveIncident = useCallback(async (incidentId: string, resolution: any) => {
    const response = await apiClient.request(`/security/incidents/${incidentId}/resolve`, {
      method: 'POST',
      body: JSON.stringify(resolution),
    });
    
    if (response.success) {
      queryClient.invalidateQueries({ queryKey: SECURITY_QUERY_KEYS.incidents() });
    }
    
    return response;
  }, [queryClient]);

  const blockIPAddress = useCallback(async (ipAddress: string, duration?: number) => {
    const response = await apiClient.request('/security/firewall/block', {
      method: 'POST',
      body: JSON.stringify({ ipAddress, duration }),
    });
    
    if (response.success) {
      queryClient.invalidateQueries({ queryKey: SECURITY_QUERY_KEYS.networkSecurity });
    }
    
    return response;
  }, [queryClient]);

  // GH #2704 removed rotateKey with the POST it wrapped. The route rotated no
  // key material -- it wrote one audit row and inferred that row's algorithm
  // and key size by substring-testing a key id the caller supplied.

  return {
    quarantineNode,
    acknowledgeAlert,
    resolveIncident,
    blockIPAddress,
  };
}

// API functions - all data comes from seeded database
async function fetchThreatDetectionMetrics(): Promise<ThreatDetectionMetrics> {
  console.log('[SECURITY] Fetching threat detection metrics...');
  const start = performance.now();
  
  try {
    const response = await apiClient.request<ThreatDetectionMetrics>('/security/threat-detection/metrics');
    const duration = performance.now() - start;
    
    console.log('[SECURITY] Threat detection metrics response in', duration.toFixed(2), 'ms:', {
      success: response.success,
      hasData: !!response.data,
      totalThreats: response.data?.overview?.totalThreats,
      activeThreats: response.data?.overview?.activeThreats
    });
    
    if (!response.success || !response.data) {
      console.error('[SECURITY] Threat detection metrics failed:', response.error);
      throw new Error(response.error || 'Failed to fetch threat detection metrics');
    }
    
    return response.data;
  } catch (error) {
    const duration = performance.now() - start;
    console.error('[SECURITY] Threat detection metrics error after', duration.toFixed(2), 'ms:', error);
    throw error;
  }
}

async function fetchThreatEvents(filters?: any): Promise<ThreatEvent[]> {
  console.log('fetchThreatEvents called with filters:', filters);
  const params = new URLSearchParams();
  if (filters?.limit) {
    params.append('limit', filters.limit.toString());
  }
  if (filters?.severity) {
    params.append('severity', filters.severity.join(','));
  }
  if (filters?.type) {
    params.append('type', filters.type.join(','));
  }
  if (filters?.status) {
    params.append('status', filters.status.join(','));
  }
  if (filters?.nodeId) {
    params.append('nodeId', filters.nodeId);
  }
  
  console.log('Fetching threat events from:', `/v1/security/threat-events?${params.toString()}`);
  const response = await apiClient.request<ThreatEvent[]>(`/security/threat-events?${params.toString()}`);
  console.log('Threat events response:', response);
  if (!response.success) {
    throw new Error(response.error || 'Failed to fetch threat events');
  }
  return response.data || [];
}

async function fetchAuthMetrics(): Promise<AuthMetrics> {
  const response = await apiClient.request<AuthMetrics>('/security/auth/metrics');
  if (!response.success) {
    throw new Error(response.error || 'Failed to fetch auth metrics');
  }
  return response.data!;
}

// Helper function to map API action to AuthEventType
function mapActionToEventType(action: string): AuthEventType {
  const actionLower = action.toLowerCase();
  if (actionLower.includes('login') && actionLower.includes('success')) return 'login';
  if (actionLower.includes('logout') || actionLower.includes('logged out')) return 'logout';
  if (actionLower.includes('token') && actionLower.includes('refresh')) return 'token_refreshed';
  if (actionLower.includes('password') && (actionLower.includes('reset') || actionLower.includes('changed'))) return 'password_changed';
  if (actionLower.includes('mfa')) return 'mfa_enabled';
  if (actionLower.includes('session') && actionLower.includes('expired')) return 'session_terminated';
  return 'login'; // Default
}

async function fetchAuthEvents(filters?: any): Promise<AuthEvent[]> {
  const params = new URLSearchParams();
  if (filters?.limit) {
    params.append('limit', filters.limit.toString());
  }
  if (filters?.result) {
    params.append('result', filters.result.join(','));
  }
  if (filters?.method) {
    params.append('method', filters.method.join(','));
  }
  if (filters?.userId) {
    params.append('userId', filters.userId);
  }
  if (filters?.nodeId) {
    params.append('nodeId', filters.nodeId);
  }
  
  const response = await apiClient.request<any[]>(`/security/auth/events?${params.toString()}`);
  if (!response.success) {
    throw new Error(response.error || 'Failed to fetch auth events');
  }
  
  // Transform API response to match AuthEvent interface
  const events = (response.data || []).map((event: any) => ({
    id: event.id,
    type: mapActionToEventType(event.action),
    userId: event.user || 'Unknown',
    username: event.user,
    nodeId: 'node-1', // Default node ID
    sourceIP: event.ip || 'Unknown',
    userAgent: event.userAgent || 'Unknown',
    method: (event.details?.includes('mfa') ? 'password' : 'password') as AuthMethod,
    result: event.success ? 'success' as AuthResult : 'failure' as AuthResult,
    timestamp: new Date(event.timestamp),
    sessionId: event.sessionId,
    mfaUsed: event.details?.includes('mfa') || false,
    riskScore: event.severity === 'critical' ? 90 : event.severity === 'high' ? 70 : 30,
    deviceFingerprint: event.sessionId,
  }));
  
  return events as unknown as AuthEvent[];
}

async function fetchCryptoOperations(filters?: any): Promise<CryptoOperation[]> {
  const params = new URLSearchParams();
  if (filters?.limit) {
    params.append('limit', filters.limit.toString());
  }
  if (filters?.type) {
    params.append('type', filters.type.join(','));
  }
  if (filters?.algorithm) {
    params.append('algorithm', filters.algorithm.join(','));
  }
  if (filters?.nodeId) {
    params.append('nodeId', filters.nodeId);
  }
  
  const response = await apiClient.request<CryptoOperation[]>(`/security/crypto/operations?${params.toString()}`);
  if (!response.success) {
    throw new Error(response.error || 'Failed to fetch crypto operations');
  }
  return response.data || [];
}

async function fetchIdentityMetrics(): Promise<IdentityMetrics> {
  const response = await apiClient.request<IdentityMetrics>('/security/identity/metrics');
  if (!response.success) {
    throw new Error(response.error || 'Failed to fetch identity metrics');
  }
  return response.data!;
}

async function fetchIdentities(filters?: any): Promise<Identity[]> {
  const params = new URLSearchParams();
  if (filters?.limit) {
    params.append('limit', filters.limit.toString());
  }
  if (filters?.status) {
    params.append('status', filters.status.join(','));
  }
  if (filters?.verificationLevel) {
    params.append('verificationLevel', filters.verificationLevel.join(','));
  }
  if (filters?.nodeId) {
    params.append('nodeId', filters.nodeId);
  }
  
  const response = await apiClient.request<Identity[]>(`/security/identities?${params.toString()}`);
  if (!response.success) {
    throw new Error(response.error || 'Failed to fetch identities');
  }
  return response.data || [];
}

async function fetchComplianceMetrics(): Promise<ComplianceMetrics> {
  const response = await apiClient.request<ComplianceMetrics>('/security/compliance/metrics');
  if (!response.success) {
    throw new Error(response.error || 'Failed to fetch compliance metrics');
  }
  return response.data!;
}

async function fetchAuditEvents(filters?: any): Promise<AuditEvent[]> {
  const params = new URLSearchParams();
  if (filters?.limit) {
    params.append('limit', filters.limit.toString());
  }
  if (filters?.category) {
    params.append('category', filters.category.join(','));
  }
  if (filters?.riskLevel) {
    params.append('riskLevel', filters.riskLevel.join(','));
  }
  if (filters?.actor) {
    params.append('actor', filters.actor);
  }
  if (filters?.nodeId) {
    params.append('nodeId', filters.nodeId);
  }
  
  const response = await apiClient.request<AuditEvent[]>(`/security/audit/events?${params.toString()}`);
  if (!response.success) {
    throw new Error(response.error || 'Failed to fetch audit events');
  }
  return response.data || [];
}

async function fetchSecurityIncidents(filters?: any): Promise<SecurityIncident[]> {
  const params = new URLSearchParams();
  if (filters?.limit) {
    params.append('limit', filters.limit.toString());
  }
  if (filters?.severity) {
    params.append('severity', filters.severity.join(','));
  }
  if (filters?.status) {
    params.append('status', filters.status.join(','));
  }
  if (filters?.assignedTo) {
    params.append('assignedTo', filters.assignedTo);
  }
  
  const response = await apiClient.request<SecurityIncident[]>(`/security/incidents?${params.toString()}`);
  if (!response.success) {
    throw new Error(response.error || 'Failed to fetch security incidents');
  }
  return response.data || [];
}

async function fetchSecurityAlerts(filters?: any): Promise<SecurityAlert[]> {
  const params = new URLSearchParams();
  if (filters?.limit) {
    params.append('limit', filters.limit.toString());
  }
  if (filters?.severity) {
    params.append('severity', filters.severity.join(','));
  }
  if (filters?.type) {
    params.append('type', filters.type.join(','));
  }
  if (filters?.unresolved) {
    params.append('unresolved', 'true');
  }
  if (filters?.nodeId) {
    params.append('nodeId', filters.nodeId);
  }
  
  const response = await apiClient.request<SecurityAlert[]>(`/security/alerts?${params.toString()}`);
  if (!response.success) {
    throw new Error(response.error || 'Failed to fetch security alerts');
  }
  return response.data || [];
}

async function fetchAnomalyDetections(filters?: any): Promise<AnomalyDetection[]> {
  const params = new URLSearchParams();
  if (filters?.limit) {
    params.append('limit', filters.limit.toString());
  }
  if (filters?.severity) {
    params.append('severity', filters.severity.join(','));
  }
  if (filters?.modelId) {
    params.append('modelId', filters.modelId);
  }
  if (filters?.nodeId) {
    params.append('nodeId', filters.nodeId);
  }
  
  const response = await apiClient.request<AnomalyDetection[]>(`/security/anomalies?${params.toString()}`);
  if (!response.success) {
    throw new Error(response.error || 'Failed to fetch anomaly detections');
  }
  return response.data || [];
}

async function fetchNetworkSecurity(): Promise<NetworkSecurityState> {
  const response = await apiClient.request<NetworkSecurityState>('/security/network');
  if (!response.success) {
    throw new Error(response.error || 'Failed to fetch network security');
  }
  return response.data!;
}

// All mock data generators removed - data comes from seeded database